import React, { useState, useImperativeHandle, forwardRef } from 'react';
import {
    Table, Button, Tag, Space, Alert, Popconfirm, Popover, Tooltip, message,
} from 'antd';
import {
    EditOutlined, DeleteOutlined, CalendarOutlined, SafetyCertificateOutlined,
} from '@ant-design/icons';
import UserAvatar from '../common/UserAvatar';
import PhoneDisplay from '../common/PhoneDisplay';
import UserModal from './modals/UserModal';
import { deleteUser } from './api';
import config from '../../config';
import UserFeatureModal from '../access-control/UserFeatureModal';
import FeatureTags from '../access-control/FeatureTags';
import { BUNDLE_BY_KEY, isGhAdminUser } from '../access-control/featureAccess';

const ROLE_LABELS = {
    [config.userRoles.ghAdmin]: 'GoHire Admin',
    [config.userRoles.companyAdmin]: 'Company Admin',
    [config.userRoles.user]: 'User',
};

const UserTable = forwardRef(({ companyId, users, onOpenCalendar, access, isGhAdmin = false }, ref) => {
    const { data, loading, error, setPage, refetch, sort, order, setSort, setOrder } = users;
    const [modalOpen, setModalOpen] = useState(false);
    const [editingUser, setEditingUser] = useState(null);

    // Access editor (gh-admin only)
    const [accessUser, setAccessUser] = useState(null);
    const [accessInit, setAccessInit] = useState({ assignments: [], overrides: { allow: [], deny: [] }, hasConnectedCalendar: false });

    const antdOrder = (o) => (o === 'desc' ? 'descend' : 'ascend');

    useImperativeHandle(ref, () => ({
        openAddModal: () => { setEditingUser(null); setModalOpen(true); },
    }));

    const handleDelete = async (id) => {
        try {
            await deleteUser(companyId, id);
            message.success('Deleted successfully');
            refetch();
        } catch (err) {
            message.error(err.response?.data?.message || 'Delete failed');
        }
    };

    const openAccess = async (record) => {
        setAccessUser(record);
        if (access) {
            const init = await access.loadUserAccess(record.id);
            setAccessInit(init);
        }
    };

    const baseColumns = [
        {
            title: '', dataIndex: 'avatar', key: 'avatar', width: 50,
            render: (_, r) => <UserAvatar name={r.name} src={r.avatar_url} userId={r.id} size={32} showTooltip />,
        },
        { title: 'Name', dataIndex: 'name', key: 'name', sorter: true, sortOrder: sort === 'name' ? antdOrder(order) : null },
        {
            title: 'Mobile', dataIndex: 'longcode_mobile', key: 'longcode_mobile', sorter: true,
            sortOrder: sort === 'longcode_mobile' ? antdOrder(order) : null, render: (v) => <PhoneDisplay value={v} />,
        },
        {
            title: 'Department', dataIndex: 'department_name', key: 'department_name', sorter: true,
            sortOrder: sort === 'department_name' ? antdOrder(order) : null,
        },
        {
            title: 'Role', dataIndex: 'role', key: 'role', sorter: true,
            sortOrder: sort === 'role' ? antdOrder(order) : null, render: (role) => ROLE_LABELS[role] || role,
        },
        {
            title: 'Status', dataIndex: 'active', key: 'active', sorter: true,
            sortOrder: sort === 'active' ? antdOrder(order) : null,
            render: (a) => <Tag color={a ? 'green' : 'default'}>{a ? 'Active' : 'Inactive'}</Tag>,
        },
    ];

    // gh-admin-only access columns
    const accessColumns = access ? [
        {
            title: 'Bundles',
            key: 'bundles',
            render: (_, r) => {
                if (isGhAdminUser(r)) return <Tag>—</Tag>;
                const keys = access.bundlesForUser(r);
                if (!keys.length) return <span style={{ color: '#bfbfbf', fontSize: 12, fontStyle: 'italic' }}>unassigned</span>;
                return <Space size={[2, 2]} wrap>{keys.map((k) => <Tag key={k} color="blue">{BUNDLE_BY_KEY[k]?.label || k}</Tag>)}</Space>;
            },
        },
        {
            title: 'Effective',
            key: 'effective',
            render: (_, r) => {
                if (isGhAdminUser(r)) return <Tag color="gold" icon={<SafetyCertificateOutlined />}>All (bypass)</Tag>;
                const eff = access.effectiveForUser(r);
                if (!eff.length) return <span style={{ color: '#bfbfbf', fontSize: 12, fontStyle: 'italic' }}>none</span>;
                return (
                    <Popover content={<div style={{ maxWidth: 280 }}><FeatureTags featureKeys={eff} /></div>} title="Effective features">
                        <Tag style={{ cursor: 'pointer' }}>{eff.length} feature{eff.length === 1 ? '' : 's'}</Tag>
                    </Popover>
                );
            },
        },
    ] : [];

    const actionColumn = {
        title: 'Actions', key: 'actions', width: access ? 150 : 120,
        render: (_, r) => (
            <Space size="small">
                {access && (isGhAdminUser(r)
                    ? (
                        <Tooltip title="GH Admins always have full access">
                            <Button type="text" size="small" icon={<SafetyCertificateOutlined />} disabled />
                        </Tooltip>
                    )
                    : (
                        <Tooltip title="Manage access">
                            <Button type="text" size="small" icon={<SafetyCertificateOutlined />} onClick={() => openAccess(r)} />
                        </Tooltip>
                    ))}
                <Button type="text" size="small" icon={<EditOutlined />} onClick={() => { setEditingUser(r); setModalOpen(true); }} />
                <Popconfirm title="Are you sure you want to delete this user?" onConfirm={() => handleDelete(r.id)} okText="Yes" cancelText="No">
                    <Button type="text" size="small" danger icon={<DeleteOutlined />} />
                </Popconfirm>
                {r.has_connected_calendar && (
                    <Button type="text" size="small" icon={<CalendarOutlined />} onClick={() => onOpenCalendar(r)} style={{ color: '#38b549' }} />
                )}
            </Space>
        ),
    };

    const columns = [...baseColumns, ...accessColumns, actionColumn];

    return (
        <div>
            {error && <Alert type="error" message={error} style={{ marginBottom: 12 }} />}

            <Table
                className="gh-dash-interview-table"
                columns={columns}
                dataSource={data.rows}
                loading={loading}
                rowKey="id"
                size="small"
                onChange={(pagination, filters, sorter) => {
                    if (sorter && sorter.order) {
                        setSort(sorter.field);
                        setOrder(sorter.order === 'ascend' ? 'asc' : 'desc');
                    } else {
                        setSort('name');
                        setOrder('asc');
                    }
                }}
                pagination={{
                    current: data.page, pageSize: data.pageSize, total: data.total,
                    onChange: (p) => setPage(p), showSizeChanger: false, size: 'small',
                }}
                locale={{
                    emptyText: !loading && data.total === 0 ? (
                        <div style={{ padding: 20 }}>
                            <p>No users found.</p>
                            <Button type="primary" icon={<EditOutlined />} onClick={() => { setEditingUser(null); setModalOpen(true); }}>
                                + Add your first user
                            </Button>
                        </div>
                    ) : 'No users found.',
                }}
            />

            <UserModal
                open={modalOpen}
                user={editingUser}
                companyId={companyId}
                isGhAdmin={isGhAdmin}
                onClose={() => { setModalOpen(false); setEditingUser(null); }}
                onSuccess={() => {
                    setModalOpen(false); setEditingUser(null); refetch();
                    // Product save auto-grants bundles server-side — refresh the
                    // Bundles/Effective columns so they reflect the new access.
                    if (access && access.refetch) access.refetch();
                }}
            />

            {access && (
                <UserFeatureModal
                    open={!!accessUser}
                    user={accessUser}
                    licenses={access.licenses}
                    initialAssignments={accessInit.assignments}
                    initialOverrides={accessInit.overrides}
                    userHasCalendar={accessInit.hasConnectedCalendar}
                    onClose={() => setAccessUser(null)}
                    onSave={access.saveUserAccess}
                />
            )}
        </div>
    );
});

UserTable.displayName = 'UserTable';

export default UserTable;
